/**
 * @file analytics.js
 * @description Client-side visitor tracker for Wikificial. Records every
 * route change to POST /api/analytics/track and fires a heartbeat every 30s
 * while the tab is visible. Persists a stable session_id in localStorage
 * with a 30-minute sliding expiry (industry-standard session definition).
 *
 * Called from a small `<AnalyticsTracker>` component mounted inside App.js.
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "wikificial_analytics_session";
const HEARTBEAT_MS = 30_000;                 // 30 s
const SESSION_TIMEOUT_MS = 30 * 60_000;      // 30 min sliding window

/**
 * @typedef {{ id: string, expires_at: number }} SessionState
 * @returns {string} session_id
 */
function getSessionId() {
  const now = Date.now();
  let saved = null;
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw) saved = JSON.parse(raw);
  } catch (_) { /* corrupted / privacy mode — start fresh */ }

  if (saved && saved.expires_at > now && saved.id) {
    // Extend sliding window
    saved.expires_at = now + SESSION_TIMEOUT_MS;
    try { localStorage.setItem(SESSION_KEY, JSON.stringify(saved)); } catch (_) {}
    return saved.id;
  }

  const id = `s_${crypto.getRandomValues(new Uint32Array(1))[0].toString(16)}_${now.toString(36)}`;
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify({ id, expires_at: now + SESSION_TIMEOUT_MS }));
  } catch (_) {}
  return id;
}

/**
 * Fire a track event for the given path.
 * @param {string} path
 * @param {{ tool_slug?: string, referrer?: string }} [extras]
 */
export async function trackPageview(path, extras = {}) {
  try {
    await axios.post(`${API}/analytics/track`, {
      session_id: getSessionId(),
      path,
      referrer: extras.referrer ?? document.referrer ?? null,
      tool_slug: extras.tool_slug ?? null,
    }, { withCredentials: true });
  } catch (e) {
    // Analytics failures must never break UX — silent fail.
  }
}

/**
 * Fire a keep-alive so "online now" reflects reality even on long-lived tabs.
 */
export async function trackHeartbeat() {
  if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
  try {
    await axios.post(`${API}/analytics/heartbeat`, {
      session_id: getSessionId(),
    }, { withCredentials: true });
  } catch (_) {}
}

/**
 * Start the periodic heartbeat. Returns a cleanup function.
 */
export function startHeartbeat() {
  const iv = setInterval(trackHeartbeat, HEARTBEAT_MS);
  const onVis = () => { if (document.visibilityState === "visible") trackHeartbeat(); };
  document.addEventListener("visibilitychange", onVis);
  return () => {
    clearInterval(iv);
    document.removeEventListener("visibilitychange", onVis);
  };
}
